<template>
  <div
      :class="[plan ? plan.toLowerCase() : 'empty']"
      class="compare-item"
  >
    <template v-if="plan">
      <div class="compare-item__header">
      <span class="compare-item__header-title">
        {{ t('plans.' + plan + '.title') }}
      </span>
        <span class="compare-item__header-subtitle">
          <ui-price
              :price="period === PlanDurationType.Monthly ?  info?.price : info?.priceAnnually"
          />
          / {{ t(period === PlanDurationType.Monthly ? 'month' : 'year') }}
        </span>
      </div>
      <p class="compare-item__row">
        {{
          info ?
              Intl.NumberFormat().format(info.maxApiRequestsCount) :
              Intl.NumberFormat().format(50000) + '+'
        }}
      </p>
      <p class="compare-item__row">
        {{
          info ?
              t('every_time', {time: getTime(info[AddonEnumLocal.MARKET_ORDER_ANALYTICS])}) :
              t('custom')
        }}
      </p>
      <p class="compare-item__row">
        {{
          info ?
              t('every_time', {time: getTime(info[AddonEnumLocal.STEAM_ANALYTICS])}) :
              t('custom')
        }}
      </p>
      <p class="compare-item__row">
        {{
          info ?
              t('every_time', {time: getTime(info[AddonEnumLocal.PRICE_RECOMMENDATIONS])}) :
              t('custom')
        }}
      </p>
      <p class="compare-item__row">
        {{
          info ?
              t('every_time', {time: getTime(info[AddonEnumLocal.MAX_ORDERS])}) :
              t('custom')
        }}
      </p>
      <p class="compare-item__row">
        {{
          info ?
              t('every_time', {time: getTime(info[AddonEnumLocal.MIN_PRICES])}) :
              t('custom')
        }}
      </p>
      <p class="compare-item__row">
        <i class="i-check green"></i>
      </p>
      <p class="compare-item__row">
        <i class="i-check green"></i>
      </p>
      <div class="compare-item__row compare-item__footer">
        <button
            :class="{'btn-violet':plan !== UserPlan.Business}"
            :disabled="plan === UserPlan.Business || plan === user?.plan"
            class="btn btn-primary fw500 w-100"
            @click="choosePlan"
        >
          {{
            t(
                plan === UserPlan.Business ? 'coming_soon' :
                    plan === user?.plan ? 'current_plan' :
                        user?.plan && user?.plan !== UserPlan.Free ? 'change_plan' :
                            'select_this_plan'
            )
          }}
        </button>
      </div>
    </template>
    <template v-else>
      <p class="compare-item__row row-hidden"></p>
      <p class="compare-item__row">
        {{ t('number_of_api_requests') }} <span> / {{ t('per_month') }} </span>
      </p>
      <p class="compare-item__row">
        {{ t('market_order_analytics_updates') }}
      </p>
      <p class="compare-item__row">
        {{ t('steam_analytics_updates') }}
      </p>
      <p class="compare-item__row">
        {{ t('price_recommendations_updates') }}
      </p>
      <p class="compare-item__row">
        {{ t('max_orders_updates') }}
      </p>
      <p class="compare-item__row">
        {{ t('min_prices_updates') }}
      </p>
      <p class="compare-item__row">
        {{ t('discord_support') }}
      </p>
      <p class="compare-item__row">
        {{ t('access_to_addons') }}
      </p>
    </template>
  </div>
</template>

<script lang="ts" setup>
import {PlanInfoLocal} from "~/data/plans";
import {PlanDurationType, UserPlan} from "~/graphql/generated/schema-types";
import {useStore} from "~/store";
import {AddonEnumLocal} from "~/data/addons";

const props = defineProps<{
  plan?: UserPlan,
  period?: PlanDurationType,
  info?: PlanInfoLocal,
}>()

const emit = defineEmits<{
  (e: 'choosePlan', plan: UserPlan): void
}>()


const {t, locale} = useI18n()
const {user} = useStore()

const choosePlan = () => {
  if (!props.plan || props.plan === UserPlan.Business) {
    return
  }
  emit('choosePlan', props.plan)
}

function getTime(time: number) {
  if (time / 3600000 < 1) {
    return t('minutes', {n: time / 60000})
  }
  return t('hours', {n: time / 3600000})
}
</script>

<style lang="scss" scoped>
.compare-item {
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 2px;
  color: var(--surfaces-on-surface);
  font-kerning: none;
  text-overflow: ellipsis;
  font-size: 14px;
  font-style: normal;
  font-weight: 400;
  line-height: 24px; /* 171.429% */
  border-radius: 12px;
  overflow: hidden;

  &.empty {
    font-weight: 600;
    margin-top: 24px;

    .compare-item__row span {
      font-weight: 400;
      color: var(--surfaces-on-surface-variable);
      margin-left: 4px;
    }
  }

  &__header {
    display: flex;
    padding: 24px 10px 20px 10px;
    flex-direction: column;
    align-items: center;
    gap: 10px;
    //border-radius: 12px 12px 0 0;
    background: var(--surfaces-low-container);
    position: relative;
    overflow: hidden;

    &-title {
      color: var(--esports);
      text-align: center;
      font-size: 18px;
      font-style: normal;
      font-weight: 600;
      line-height: 24px; /* 133.333% */
    }

    &-subtitle {
      color: var(--surfaces-on-surface-variable);
      font-size: 14px;
      font-style: normal;
      font-weight: 400;
      line-height: 24px; /* 171.429% */
      &:deep(.price) {
        color: var(--surfaces-on-surface);
        font-size: 14px;
        font-style: normal;
        font-weight: 600;
        line-height: 18px; /* 128.571% */
      }
    }

    &:after {
      content: '';
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      height: 4px;
      background: var(--esports);
    }
  }

  &.pro .compare-item__header {
    &-title {
      color: var(--hardware);
    }

    &:after {
      background: var(--hardware);
    }
  }

  &.business .compare-item__header {
    &-title {
      color: var(--csmc-updates);
    }

    &:after {
      background: var(--csmc-updates);
    }
  }

  &__row {
    padding: 20px;

    &.row-hidden {
      display: none;
    }

    &:nth-child(odd) {
      background: var(--surfaces-container);
    }

    &:nth-child(even) {
      background: var(--surfaces-low-container);
    }
  }

  &__footer {
    padding: 20px;
    //border-radius: 0 0 12px 12px;
    background: var(--surface-low-container);
  }
}
</style>
